import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, History, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  type DataTableColumn,
} from "@/components/shared";
import { useAdminWalletTransactions } from "@/hooks/queries";
import { TransactionCategory, TransactionType } from "@/types/enums";
import type { WalletTransactionResponseDto } from "@/types/wallet.dto";
import { cn } from "@/lib/utils";

const categoryOptions: { value: string; label: string }[] = [
  { value: "all", label: "All categories" },
  { value: String(TransactionCategory.OrderPayment), label: "Order Payment" },
  { value: String(TransactionCategory.OrderRefund), label: "Refund" },
  { value: String(TransactionCategory.WalletTopUp), label: "Wallet Top-up" },
  { value: String(TransactionCategory.AdminAdjustment), label: "Admin Adjustment" },
  { value: String(TransactionCategory.P2PTransferIn), label: "P2P In" },
  { value: String(TransactionCategory.P2PTransferOut), label: "P2P Out" },
  { value: String(TransactionCategory.SelfCashback), label: "Self Cashback" },
  { value: String(TransactionCategory.LevelCashback), label: "Level Cashback" },
  { value: String(TransactionCategory.RewardCashback), label: "Reward" },
  { value: String(TransactionCategory.Withdrawal), label: "Withdrawal" },
];

const AdminWalletTransactions = () => {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const filter = useMemo(
    () => ({
      search: appliedSearch.trim() || undefined,
      type: typeFilter === "all" ? undefined : (Number(typeFilter) as TransactionType),
      category: categoryFilter === "all" ? undefined : (Number(categoryFilter) as TransactionCategory),
      page,
      pageSize,
    }),
    [appliedSearch, typeFilter, categoryFilter, page, pageSize],
  );

  const { data, isLoading, isFetching } = useAdminWalletTransactions(filter);
  const items = data?.items ?? [];

  const stats = useMemo(() => {
    const credits = items.filter((t) => t.type === TransactionType.Credit).reduce((s, t) => s + t.amount, 0);
    const debits = items.filter((t) => t.type === TransactionType.Debit).reduce((s, t) => s + t.amount, 0);
    return { credits, debits, count: data?.totalItems ?? 0 };
  }, [items, data?.totalItems]);

  const columns: DataTableColumn<WalletTransactionResponseDto>[] = [
    {
      id: "txn",
      header: "Transaction",
      cell: (row) => (
        <div>
          <p className="font-mono text-xs font-medium">{row.transactionId}</p>
          <p className="text-xs text-muted-foreground">{row.categoryText}</p>
        </div>
      ),
    },
    {
      id: "user",
      header: "User",
      cell: (row) => (
        <div>
          <p className="text-sm font-medium">{row.userName || "—"}</p>
          <p className="text-xs text-muted-foreground">{row.userEmail}</p>
          {row.memberCode && <p className="font-mono text-[11px] text-muted-foreground">{row.memberCode}</p>}
          {row.userId && (
            <Button variant="link" className="h-auto p-0 text-xs" asChild>
              <Link to={`/admin/wallet/users/${row.userId}`}>View wallet</Link>
            </Button>
          )}
        </div>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: (row) => (
        <Badge variant={row.type === TransactionType.Credit ? "default" : "secondary"} className="gap-1">
          {row.type === TransactionType.Credit ? (
            <ArrowUpRight className="h-3 w-3" />
          ) : (
            <ArrowDownRight className="h-3 w-3" />
          )}
          {row.typeText}
        </Badge>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      align: "right",
      cell: (row) => (
        <span
          className={cn(
            "font-semibold",
            row.type === TransactionType.Credit ? "text-success" : "text-destructive",
          )}
        >
          {row.type === TransactionType.Credit ? "+" : "-"}₹{row.amount.toFixed(2)}
        </span>
      ),
    },
    {
      id: "balance",
      header: "Balance after",
      align: "right",
      cell: (row) => <span className="text-sm">₹{row.balanceAfter.toFixed(2)}</span>,
    },
    {
      id: "desc",
      header: "Description",
      cell: (row) => <p className="max-w-[240px] truncate text-xs text-muted-foreground">{row.description}</p>,
    },
    {
      id: "date",
      header: "Date",
      cell: (row) => (
        <span className="text-xs text-muted-foreground">
          {new Date(row.createdAt).toLocaleString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Wallet Transactions"
        description="Ledger of all user wallet credits and debits (orders, top-ups, P2P, cashback)."
      />

      <StatsGrid columns={3}>
        <CompactStatCard title="Total records" value={stats.count} icon={History} />
        <CompactStatCard title="Credits (page)" value={`₹${stats.credits.toFixed(0)}`} icon={ArrowUpRight} />
        <CompactStatCard title="Debits (page)" value={`₹${stats.debits.toFixed(0)}`} icon={ArrowDownRight} />
      </StatsGrid>

      <div className="flex flex-col gap-3 lg:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-10 pl-9"
            placeholder="Search txn id, description, user id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                setAppliedSearch(search);
              }
            }}
          />
        </div>
        <Select
          value={typeFilter}
          onValueChange={(v) => {
            setTypeFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-10 w-full lg:w-[140px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value={String(TransactionType.Credit)}>Credit</SelectItem>
            <SelectItem value={String(TransactionType.Debit)}>Debit</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={categoryFilter}
          onValueChange={(v) => {
            setCategoryFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-10 w-full lg:w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {categoryOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          className="h-10"
          onClick={() => {
            setPage(1);
            setAppliedSearch(search);
          }}
        >
          Search
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
        <div className={isFetching ? "opacity-70" : undefined}>
          <DataTable
            columns={columns}
            data={items}
            getRowId={(row) => String(row.id)}
            emptyMessage="No wallet transactions found."
            pagination={{
              page,
              pageSize: data?.pageSize ?? pageSize,
              totalCount: data?.totalItems ?? 0,
              totalPages: data?.totalPages ?? 1,
              from: data?.totalItems ? (page - 1) * (data?.pageSize ?? pageSize) + 1 : 0,
              to: Math.min(page * (data?.pageSize ?? pageSize), data?.totalItems ?? 0),
            }}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AdminWalletTransactions;
